import adPodsEnabled from "../rules/adpods";
import { pushAdStage } from "../services/ad-stage-tracker";
import type { IBid } from "../services/provider";
import { VideoReporter } from "../services/video-error-reporter";
import type {
  IAdsService,
  VideoRuntime,
} from "../types/IAdsService";
import type { ResolvedVideoConfig } from "../types/config";
import type { VideoAdFinishReason, VideoAdOutcome } from "../types/outcome";
import {
  createInitialVideoAdState,
  videoAdReducer,
  type VideoAdEvent,
  type VideoAdState,
} from "./video-ad-state";

export type { VideoAdFinishReason, VideoAdOutcome } from "../types/outcome";

export interface VideoAdSessionClock {
  setInterval(callback: () => void, milliseconds: number): ReturnType<typeof setInterval>;
  clearInterval(timer: ReturnType<typeof setInterval>): void;
}

export interface VideoAdSessionOptions {
  config: ResolvedVideoConfig;
  service: IAdsService;
  clock?: VideoAdSessionClock;
}

type Listener = (state: VideoAdState) => void;

export interface VideoPlaybackControls {
  start?(): void;
  pause(): void;
  resume(): void;
}

const defaultClock: VideoAdSessionClock = {
  setInterval: (callback, milliseconds) => setInterval(callback, milliseconds),
  clearInterval: (timer) => clearInterval(timer),
};

export class VideoAdSession {
  private state: VideoAdState;
  private readonly listeners = new Set<Listener>();
  private readonly service: IAdsService;
  private readonly clock: VideoAdSessionClock;
  private timer?: ReturnType<typeof setInterval>;
  private playbackControls?: VideoPlaybackControls;
  private initialization?: Promise<void>;
  private settled = false;
  private destroyed = false;
  private resolveOutcome!: (outcome: VideoAdOutcome) => void;

  readonly finished: Promise<VideoAdOutcome>;

  constructor({ config, service, clock = defaultClock }: VideoAdSessionOptions) {
    this.state = createInitialVideoAdState(config);
    this.service = service;
    this.clock = clock;
    this.finished = new Promise((resolve) => {
      this.resolveOutcome = resolve;
    });
  }

  getState = (): VideoAdState => this.state;

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  initialize = (): Promise<void> => {
    if (this.initialization) return this.initialization;

    this.initialization = this.load().catch(() => {
      if (!this.destroyed) this.enterFallback(20);
    });
    return this.initialization;
  };

  markPlayerLoaded = (): void => {
    if (!this.state.playerLoaded) this.dispatch({ type: "player-ready" });
  };

  startVideo = async (): Promise<void> => {
    const { bids, config, phase, playerLoaded } = this.state;
    if (phase !== "cta" || !playerLoaded || bids === null) return;

    config.container.classList.add("playing");
    config.onCTAStateChange({ isShowing: false });
    this.playbackControls?.start?.();
    this.startCountdown();
    await this.playNextBid(bids, false);
  };

  adPodsAllowed = (): boolean =>
    this.state.config.adPodding && this.service.useAdPods === true;

  videoCompleted = async (): Promise<void> => {
    const { config, generalTimeLeft, videoAdCount } = this.state;
    const useAdPods =
      this.adPodsAllowed() &&
      !config.isDev &&
      generalTimeLeft > 10 &&
      adPodsEnabled() &&
      videoAdCount < 4;

    if (!useAdPods) {
      this.finish("completed");
      return;
    }

    const bids = await this.service.request({
      ...config,
      duration: generalTimeLeft,
    });
    if (this.destroyed) return;
    this.dispatch({ type: "ad-pod-started", bids });
    await this.playNextBid(bids, false);
  };

  videoError = async (
    error: unknown,
    details?: { player?: string },
  ): Promise<void> => {
    if (this.state.phase !== "video" && this.state.phase !== "starting") return;

    const remainingBids = this.state.bids ?? [];
    VideoReporter.handleError(
      error,
      details,
      this.state.videoRetryCount,
      [...remainingBids],
      this.state.currentBid,
    );
    await this.playNextBid(remainingBids, true);
  };

  setVolume = (volume: number): void => {
    const normalized = Math.min(1, Math.max(0, volume));
    this.dispatch({ type: "volume-changed", volume: normalized });
  };

  setPaused = (paused: boolean): void => {
    this.dispatch({ type: "playback-changed", paused });
  };

  registerPlaybackControls = (
    controls: VideoPlaybackControls,
  ): (() => void) => {
    this.playbackControls = controls;
    return () => {
      if (this.playbackControls === controls) this.playbackControls = undefined;
    };
  };

  pause = (): void => this.playbackControls?.pause();

  resume = (): void => this.playbackControls?.resume();

  togglePlayback = (): void => {
    if (this.state.videoPaused) this.resume();
    else this.pause();
  };

  markImaFailed = (): void => this.dispatch({ type: "ima-failed" });

  skip = (): void => this.finish("completed");

  finish = (reason: VideoAdFinishReason = "completed"): void => {
    if (this.settled) return;
    this.settled = true;
    this.stopCountdown();
    this.dispatch({ type: "finished" });

    try {
      if (reason === "completed") this.state.config.finalCallback();
    } finally {
      void this.service.cleanup();
      this.resolveOutcome({ reason });
    }
  };

  destroy = (): void => {
    if (this.destroyed) return;
    this.destroyed = true;
    this.listeners.clear();
    this.finish("destroyed");
  };

  private async load(): Promise<void> {
    const runtime: VideoRuntime = this.service.initialize
      ? await this.service.initialize()
      : {
          player: "ima",
          fallback: this.service.useH5 ? "h5" : "display",
        };
    if (this.destroyed) return;
    this.dispatch({ type: "runtime-ready", runtime });

    const bids = await this.service.request(this.state.config);
    if (this.destroyed) return;
    this.dispatch({ type: "bids-ready", bids });
  }

  private async playNextBid(
    bids: readonly IBid[],
    retrying: boolean,
  ): Promise<void> {
    const [bid, ...remainingBids] = bids;
    if (!bid) {
      pushAdStage("no_bids");
      this.enterFallback(retrying ? 10 : 20);
      return;
    }

    this.dispatch(
      retrying
        ? { type: "retrying", remainingBids }
        : { type: "bid-starting", remainingBids },
    );
    VideoReporter.setBidderContext(bid);

    try {
      const url = await bid.provider.buildVideoUrl(
        bid,
        this.state.videoIMAFailed,
      );
      if (this.destroyed || this.settled) return;
      pushAdStage(retrying ? "retrying_next_bid" : "video_url_ready");
      this.dispatch({
        type: "video-ready",
        bid: { bidder: bid.provider.name, cpm: bid.bid.cpm, url },
      });
    } catch (error) {
      if (this.destroyed || this.settled) return;
      await this.videoError(error, { player: "URL" });
    }
  }

  private enterFallback(duration: number): void {
    if (this.destroyed || this.settled) return;
    this.dispatch({ type: "fallback", duration });
    this.startCountdown();
  }

  private startCountdown(): void {
    if (this.timer) return;
    this.timer = this.clock.setInterval(() => {
      if (this.destroyed || this.settled) return;
      this.dispatch({ type: "tick" });
      if (this.state.generalTimeLeft <= 0) this.finish("completed");
    }, 1000);
  }

  private stopCountdown(): void {
    if (!this.timer) return;
    this.clock.clearInterval(this.timer);
    this.timer = undefined;
  }

  private dispatch(event: VideoAdEvent): void {
    const nextState = videoAdReducer(this.state, event);
    if (nextState === this.state) return;
    this.state = nextState;
    this.listeners.forEach((listener) => listener(nextState));
  }
}
